import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import API from "../api";
import { CURRENT_SEASON } from "../constants/season";
import { COMPETITION_OPTIONS } from "../constants/competitionPrefs";

function movementLine(places) {
  if (places == null || Number.isNaN(Number(places))) return null;
  const n = Number(places);
  if (n > 0) return `Up ${n} ${n === 1 ? "place" : "places"}`;
  if (n < 0) return `Down ${Math.abs(n)} ${Math.abs(n) === 1 ? "place" : "places"}`;
  return "Same place";
}

function callLine(call) {
  if (!call) return null;
  return `${call.home_team} ${call.home_score}–${call.away_score} ${call.away_team} · you ${call.predicted_home_score}–${call.predicted_away_score} · ${call.points}`;
}

export default function MatchdayRecapCard({
  competition = "premier_league",
  season = CURRENT_SEASON,
  refreshMs = 0,
}) {
  const [copied, setCopied] = useState(false);
  const label = COMPETITION_OPTIONS.find((c) => c.id === competition)?.label || "League";

  const { data, isLoading } = useQuery({
    queryKey: ["matchdayRecap", competition, season],
    queryFn: () =>
      API.get("/predictions/matchday-recap", { params: { competition, season } }).then((res) => res.data),
    staleTime: 30 * 1000,
    refetchInterval: refreshMs > 0 ? refreshMs : false,
  });

  if (isLoading || !data || data.empty) return null;

  const moved = movementLine(data.places);
  const shareText = [
    `Predict90 · ${label} matchday ${data.matchday}`,
    `${data.points} pts${data.rank != null ? ` · rank #${data.rank}` : ""}${moved ? ` · ${moved}` : ""}`,
    data.best ? `Best: ${callLine(data.best)}` : null,
    data.worst ? `Worst: ${callLine(data.worst)}` : null,
    `${data.roundsCompleted} rounds completed`,
  ]
    .filter(Boolean)
    .join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-gray-100">
        <h2 className="text-[15px] font-semibold text-ink">Last round</h2>
        <button type="button" onClick={copy} className="text-meta font-semibold text-brand">
          {copied ? "Copied" : "Copy recap"}
        </button>
      </div>
      <div className="p-4 md:p-5 space-y-3">
        <p className="text-[17px] font-semibold text-ink">
          {data.points} pts
          {data.rank != null && (
            <span className="text-meta font-medium text-muted"> · #{data.rank}{data.players ? ` of ${data.players}` : ""}</span>
          )}
          {moved && <span className="text-meta font-semibold text-ink"> · {moved}</span>}
        </p>
        <p className="text-[12px] text-muted">{label} · matchday {data.matchday} · {data.roundsCompleted} rounds completed</p>
        {data.best && (
          <p className="text-sm text-ink">
            <span className="text-muted">Best </span>
            {callLine(data.best)}
          </p>
        )}
        {data.worst && (
          <p className="text-sm text-ink">
            <span className="text-muted">Worst </span>
            {callLine(data.worst)}
          </p>
        )}
      </div>
    </section>
  );
}
